export const AUTH_INTENDED_URL_KEY = 'landi-canvas-auth-intended-url';

/** Canonical Vite dev origin — PKCE verifier and callback must share this host. */
export const CANONICAL_DEV_ORIGIN = 'http://localhost:5173';

const LOCAL_AUTH_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]']);

const ALT_DEV_HOSTS = new Set(['127.0.0.1', '0.0.0.0', '[::1]']);

/** True when the current page is served from a local dev host. */
function isLocalOrigin(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return LOCAL_AUTH_HOSTS.has(window.location.hostname);
}

/**
 * OAuth/email callback URL for Supabase `redirectTo`.
 *
 * Always returns to the ACTUAL originating origin so a `:5173` Vite dev server
 * returns to `:5173` (not the `VITE_SITE_URL` canonical `:3000`). On local dev
 * hosts we ignore `VITE_SITE_URL` entirely; on deployed hosts (pages.dev) we
 * honor `VITE_SITE_URL` when set, else fall back to the live origin. The target
 * origin must be present in the Supabase Auth redirect allowlist.
 */
/**
 * In dev, redirect alternate loopback hosts to localhost before Supabase init.
 * Skips /auth/callback so an in-flight OAuth return can exchange on the same
 * origin that stored the PKCE verifier (legacy 127.0.0.1 sessions).
 */
export function enforceCanonicalDevOrigin(): void {
  if (!import.meta.env.DEV || typeof window === 'undefined') {
    return;
  }
  const { hostname, port, protocol, pathname, search, hash } = window.location;
  if (protocol !== 'http:' || port !== '5173' || !ALT_DEV_HOSTS.has(hostname)) {
    return;
  }
  if (pathname === '/auth/callback' || pathname.startsWith('/auth/callback/')) {
    return;
  }
  const path = `${pathname}${search}${hash}`;
  window.location.replace(`${CANONICAL_DEV_ORIGIN}${path}`);
}

/** Map Supabase auth errors to clearer local-dev guidance. */
export function formatAuthErrorMessage(message: string): string {
  if (/rate limit/i.test(message)) {
    return (
      'Too many sign-in attempts for this Supabase project. Wait a few minutes, then try again. ' +
      'Production (https://landi-canvas-studio.pages.dev/) shares the same auth pool — repeated local ' +
      'dev or E2E tests can trigger this limit.'
    );
  }
  if (/pkce.*verifier.*not found|invalid flow state|no valid flow state found/i.test(message)) {
    return (
      `${message} In Vite dev, React StrictMode can double-run the OAuth callback — ` +
      'refresh and sign in again. If it persists, clear site data for this origin and retry. ' +
      `Use ${CANONICAL_DEV_ORIGIN} only (not 127.0.0.1).`
    );
  }
  return message;
}

export function getAuthCallbackUrl(): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  if (isLocalOrigin() && origin) {
    return `${origin}/auth/callback`;
  }

  const siteUrl = import.meta.env.VITE_SITE_URL?.replace(/\/$/, '');
  if (siteUrl) {
    return `${siteUrl}/auth/callback`;
  }
  return `${origin}/auth/callback`;
}

export function getUserInitials(user: { email?: string | null; user_metadata?: Record<string, unknown> }): string {
  const name = typeof user.user_metadata?.full_name === 'string' ? user.user_metadata.full_name : '';
  if (name.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const email = user.email ?? '';
  if (email.includes('@')) {
    return email.split('@')[0]?.slice(0, 2).toUpperCase() ?? 'U';
  }
  return 'U';
}

export function getUserDisplayName(user: { email?: string | null; user_metadata?: Record<string, unknown> }): string {
  const name = typeof user.user_metadata?.full_name === 'string' ? user.user_metadata.full_name.trim() : '';
  if (name) return name;
  const email = user.email ?? '';
  if (email.includes('@')) {
    const local = email.split('@')[0] ?? email;
    return local.length > 12 ? `${local.slice(0, 10)}…` : local;
  }
  return 'Account';
}
